<script setup>
import BentoShowcaseCards from '@/Components/BentoShowcaseCards.vue';
import AnimatedWords from '@/Components/AnimatedWords.vue';
import Button from '@/Components/Button.vue';
import ServiceShowcase from '@/Components/ServiceShowcase.vue';
import TechStackSection from '@/Components/TechStackSection.vue';
import TeamMembersSection from '@/Components/TeamMembersSection.vue'; // Add this import
import TestimonialCarousel from '@/Components/TestimonialCarousel.vue';
import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue'
import { products } from '@/data/products'
import { ref, onMounted, computed } from 'vue';
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(ScrollTrigger, CustomEase)

const heroTextRef = ref(null)
const heroSubtitleRef = ref(null)
const heroCtaRef = ref(null)
const heroContactInfoRef = ref(null)
const heroImageRef = ref(null)
const heroStartAnimation = ref(null)
const heroOverlayRef = ref(null)
const heroImageContainerRef = ref(null)
const footerTitleRef = ref(null)
const footerSubtitleRef = ref(null)
const footerButtonRef = ref(null)
const footerStartTextAnimation = ref(null)

const featuresListRef = ref(null)

onMounted(() => {
    // Simple fade-in animations without complex scroll triggers
    gsap.fromTo(heroImageRef.value, { opacity: 0, scale: 0.95, y: 30 }, { opacity: 1, scale: 1, y: 0, duration: 1.5, ease: "power3.out", delay: 0.3 })
    gsap.fromTo([heroTextRef.value, heroSubtitleRef.value, heroCtaRef.value, heroContactInfoRef.value], { opacity: 0, y: 40 },{ opacity: 1, y: 0, duration: 1, stagger: 0.2, ease: "power3.out", delay: 1 })

    // Scroll-triggered overlay fade out
    gsap.fromTo(heroOverlayRef.value, { opacity: 1 }, { opacity: 0, ease: "power2.out", scrollTrigger: { trigger: document.body, start: "top", end: "100vh top", scrub: true, }})

    // Scroll-triggered image height expansion to 100vh
    gsap.fromTo(heroImageContainerRef.value, { height: "auto" }, { height: "100vh", ease: "power2.out", scrollTrigger: { trigger: document.body, start: "top", end: "100vh top", scrub: true, }})

    const featureItems = featuresListRef.value.querySelectorAll('.feature-item');
    gsap.fromTo(featureItems, { opacity: 0, y: 80, scale: 0.6, rotationX: 20, transformOrigin: "center bottom" }, { opacity: 1, y: 0, scale: 1, rotationX: 0, duration: 0.5, stagger: { amount: 0.7, from: "start", grid: "auto", ease: "power1.out" }, ease: "back.out(0.25)", scrollTrigger: { trigger: featuresListRef.value, start: "top 80%", end: "+=300", toggleActions: "play none none none", id: "features-animation" } });

    gsap.fromTo(footerTitleRef.value, { y: '10vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: footerStartTextAnimation.value, start: 'top 50%', end: 'top 0%', scrub: true, invalidateOnRefresh: true, }});
    gsap.fromTo(footerSubtitleRef.value, { y: '+10vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: footerStartTextAnimation.value, start: 'top 65%', end: 'top 5%', scrub: true, invalidateOnRefresh: true, }});
    gsap.fromTo(footerButtonRef.value, { y: '+15vh', opacity: 0 }, { y: 0, opacity: 1, duration: 2, ease: 'power2.out', scrollTrigger: { trigger: footerStartTextAnimation.value, start: 'top 65%', end: 'top 10%', scrub: true, invalidateOnRefresh: true, }});
})

const features = [
    {
        name: 'Naadloze integratie',
        longName: 'Verbind systemen met naadloze integraties',
        shortDescription: 'Verbind al je bestaande systemen en diensten in één gestroomlijnd platform.',
        longDescription:
        'Onze platforms integreren naadloos met je bestaande software, CRM-systemen, en databases. Bij MijnAutoSnelweg hebben we bijvoorbeeld verbindingen gemaakt met RDW, verzekeringssystemen en dealerbeheerssoftware om een compleet ecosysteem te creëren.',
        href: '#',
        icon: 'link',
        showcase: {
            1: {
                alt: 'Systeem integratie visualisatie',
                image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'API integratie dashboard',
                image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'Systeem connectiviteit overzicht',
                image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
        },
    },
    {
        name: 'App & Web synergie',
        longName: 'Cross-platform functionaliteit voor apps en web',
        shortDescription: 'Synchroniseer je mobiele en webapplicaties voor een consistente gebruikerservaring.',
        longDescription:
        'Onze platforms zijn ontworpen om naadloos te functioneren op zowel mobiele als webomgevingen. Dit zorgt voor een consistente gebruikerservaring, ongeacht het apparaat. In SamSam hebben we bijvoorbeeld een app ontwikkeld die volledig synchroniseert met de webinterface, zodat studenten en docenten altijd up-to-date zijn.',
        href: '#',
        icon: 'mobile',
        showcase: {
            1: {
                alt: 'Mobile and web interface syncing',
                image: "https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'App interface design',
                image: "https://images.unsplash.com/photo-1556155092-490a1ba16284?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'Cross-platform functionality',
                image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Overview',
                description: 'Compleet overzicht van alle interacties'
            },
        },
    },
    {
        name: 'Maatwerkoplossingen',
        longName: 'Op maat gemaakte oplossingen voor je specifieke uitdagingen',
        shortDescription: 'Gespecialiseerde platforms die precies doen wat je business nodig heeft.',
        longDescription:
        'Elk platform wordt gebouwd voor je specifieke behoeften en processen. Voor MijnAutoSnelweg hebben we een geavanceerd veilingsysteem ontwikkeld dat autohandelaren in staat stelt sneller en efficiënter voertuigen te vinden en aan te schaffen.',
        href: '#',
        icon: 'tools',
        showcase: {
            1: {
                alt: 'Custom solution architecture',
                image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'Specialized interface elements',
                image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?q=80&w=2936&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'Custom workflow implementation',
                image: "https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=2864&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Overview',
                description: 'Compleet overzicht van alle interacties'
            },
        },
    },
    {
        name: 'UX Optimalisatie',
        longName: 'Gebruikservaring die indruk maakt en converteert',
        shortDescription: 'Intuïtieve interfaces die gebruikers direct begrijpen en waarderen.',
        longDescription:
        'Gebruiksvriendelijke platforms die complex werk eenvoudig maken. In SamSam hebben we speciale aandacht besteed aan het maken van een interface die zowel door studenten als docenten intuïtief gebruikt kan worden, waardoor de adoptie en tevredenheid sterk is toegenomen.',
        href: '#',
        icon: 'hand-pointer',
        showcase: {
            1: {
                alt: 'User flow optimization',
                image: "https://images.unsplash.com/photo-1587440871875-191322ee64b0?q=80&w=2942&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'Interface design elements',
                image: "https://images.unsplash.com/photo-1545235617-9465d2a55698?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'User experience testing',
                image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Overview',
                description: 'Compleet overzicht van alle interacties'
            },
        },
    },
    {
        name: 'Databeveiliging',
        longName: 'Geavanceerde databeveiliging en privacy-controles',
        shortDescription: 'Je gegevens en die van je klanten blijven veilig en beschermd.',
        longDescription:
        'Implementatie van de nieuwste beveiligingsstandaarden met speciale aandacht voor dataprivacy en GDPR-compliance. Voor platforms zoals MijnAutoSnelweg, waar gevoelige financiële transacties plaatsvinden, hebben we extra beveiligingslagen toegevoegd om gebruikers te beschermen.',
        href: '#',
        icon: 'shield-alt',
        showcase: {
            1: {
                alt: 'Security implementation',
                image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'Data protection measures',
                image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2832&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'Privacy controls interface',
                image: "https://images.unsplash.com/photo-1560758526-08d934e5a0a1?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Overview',
                description: 'Compleet overzicht van alle interacties'
            },
        },
    },
    {
        name: 'Realtime updates',
        longName: 'Directe synchronisatie en realtime gegevensupdates',
        shortDescription: 'Informatie is altijd actueel, op elk apparaat en voor elke gebruiker.',
        longDescription:
        'Onze platforms bieden realtime synchronisatie zodat alle gebruikers direct toegang hebben tot de meest actuele informatie. In SamSam kunnen docenten direct zien wanneer studenten opdrachten voltooien en feedback geven die onmiddellijk zichtbaar is voor de student.',
        href: '#',
        icon: 'sync',
        showcase: {
            1: {
                alt: 'Real-time data synchronization',
                image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Hoofdbeeld',
                description: 'Centrale omgeving voor alle systemen'
            },
            2: {
                alt: 'Live update notification',
                image: "https://images.unsplash.com/photo-1586282391129-76a6df230234?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Detail',
                description: 'Verbindingen in real-time visualiseren'
            },
            3: {
                alt: 'Synchronized devices',
                image: "https://images.unsplash.com/photo-1581093458791-9d3ec9cb5961?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                title: 'Overview',
                description: 'Compleet overzicht van alle interacties'
            },
        },
    },
]

// Define service categories for the ServiceShowcase component
const serviceCategories = [
  {
    name: 'Software Oplossingen',
    services: [
      {
        title: 'Klant- & Beheerportalen',
        shortDescription: 'Centrale omgevingen voor klanten en beheerders met gepersonaliseerde functionaliteiten.',
        description: 'Onze portalen bieden een gebruiksvriendelijke interface voor klanten, medewerkers en beheerders. Met gepersonaliseerde dashboards, realtime inzichten en gestroomlijnde werkprocessen verbetert u de efficiëntie en tevredenheid van alle stakeholders. Ideaal voor bedrijven die hun klantenservice willen professionaliseren.',
        image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/portalen',
        ctaText: 'Ontdek portaaloplossingen'
      },
      {
        title: 'Mobile Apps',
        shortDescription: 'Native en hybride apps die uw diensten toegankelijk maken op elk apparaat.',
        description: 'Breng uw diensten naar de smartphone van uw klanten. Onze apps synchroniseren naadloos met uw bestaande systemen, bieden offline functionaliteit en verzekeren een consistente gebruikerservaring op elk apparaat. Perfect voor bedrijven die hun bereik willen vergroten en klantbetrokkenheid willen verhogen.',
        image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/apps',
        ctaText: 'Bekijk app ontwikkeling'
      },
      {
        title: 'Websites & CMS',
        shortDescription: 'Moderne websites met krachtige content management systemen.',
        description: 'Van corporate websites tot complexe webapplicaties – wij bouwen schaalbare oplossingen met intuïtieve content management systemen. Focus op uw content terwijl wij zorgen voor technische excellentie, snelheid en veiligheid. Geschikt voor elk type organisatie die een professionele online aanwezigheid wil.',
        image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/websites',
        ctaText: 'Ontdek weboplossingen'
      },
      {
        title: 'E-commerce Oplossingen',
        shortDescription: 'Webshops en marktplaatsen die conversie en gebruiksgemak centraal stellen.',
        description: 'Verkoop uw producten en diensten online met een platform dat groeit met uw business. Onze e-commerce oplossingen integreren naadloos met betalingsproviders, voorraadbeheer en CRM-systemen voor een volledig geautomatiseerd verkoopproces. Van startups tot enterprise-niveau ondersteund.',
        image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/ecommerce',
        ctaText: 'Bekijk e-commerce opties'
      },
      {
        title: 'Data Management Platform',
        shortDescription: 'Centraliseer, verwerk en analyseer al uw bedrijfsdata.',
        description: 'Haal waarde uit uw data met een gecentraliseerd platform. Visualiseer trends, genereer rapporten en neem datagedreven beslissingen met onze op maat gemaakte data management oplossingen die voldoen aan de hoogste beveiligingsstandaarden. Transformeer data in strategische inzichten voor uw bedrijf.',
        image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/data-management',
        ctaText: 'Data management oplossingen'
      }
    ]
  },
  {
    name: 'Digitale Transformatie',
    services: [
      {
        title: 'Strategie & Advies',
        shortDescription: 'Strategisch advies om uw digitale transformatie te versnellen.',
        description: 'Onze experts helpen u bij het bepalen van de digitale strategie die past bij uw business doelen. We identificeren kansen, adviseren over technologiekeuzes en stellen een roadmap op voor succesvolle implementatie. Van quick wins tot langetermijnvisie - wij begeleiden u door elke fase van digitalisering.',
        image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/strategie',
        ctaText: 'Meer over strategie'
      },
      {
        title: 'Technische Architectuur',
        shortDescription: 'Robuuste architectuur voor schaalbare en toekomstbestendige systemen.',
        description: 'Wij ontwerpen technische architecturen die betrouwbaarheid, schaalbaarheid en onderhoudbaarheid garanderen. Een solide fundament is cruciaal voor het succes van uw digitale oplossingen op lange termijn. We zorgen ervoor dat uw systemen kunnen groeien met uw ambities zonder technische schuld.',
        image: 'https://images.unsplash.com/photo-1558403194-611308249627?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/architectuur',
        ctaText: 'Technische architectuur'
      },
      {
        title: 'Data & AI Integratie',
        shortDescription: 'Integreer AI en machine learning in uw bedrijfsprocessen.',
        description: 'Versterk uw business met kunstmatige intelligentie en machine learning. Van voorspellende analyses tot geautomatiseerde besluitvorming – onze data scientists en developers implementeren AI-oplossingen die concrete waarde toevoegen. We maken complexe technologie toegankelijk en praktisch toepasbaar.',
        image: 'https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/data-ai',
        ctaText: 'Ontdek Data & AI mogelijkheden'
      },
      {
        title: 'Software Migratie',
        shortDescription: 'Neem bestaande software over en moderniseer waar nodig.',
        description: 'Wij nemen het onderhoud en de doorontwikkeling van uw bestaande software over. Na een grondige code review werken we aan prestatieverbetering, stabiliteit en nieuwe functionaliteiten om uw software toekomstbestendig te maken. Minimale downtime, maximale verbetering.',
        image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/software-migratie',
        ctaText: 'Software migratie diensten'
      },
      {
        title: 'Replatforming',
        shortDescription: 'Vernieuw verouderde systemen met moderne technologie.',
        description: 'Moderniseer uw legacy systemen zonder functionaliteit te verliezen. Onze replatforming aanpak zorgt voor een soepele transitie naar moderne technologieën, betere prestaties en lagere onderhoudskosten. We behouden wat werkt en verbeteren wat nodig is voor toekomstige groei.',
        image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/replatforming',
        ctaText: 'Lees over replatforming'
      }
    ]
  },
  {
    name: 'Innovatie & Development',
    services: [
      {
        title: 'API Ontwikkeling',
        shortDescription: 'Verbind systemen en applicaties met robuuste APIs.',
        description: 'Onze API-ontwikkeling zorgt voor naadloze integratie tussen systemen en applicaties. We bouwen veilige, schaalbare en goed gedocumenteerde APIs die de basis vormen voor een flexibele IT-infrastructuur. Van RESTful services tot GraphQL - we kiezen de juiste technologie voor uw use case.',
        image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/api-ontwikkeling',
        ctaText: 'API development services'
      },
      {
        title: 'DevOps & Cloud',
        shortDescription: 'Geautomatiseerde deployment en beheer in de cloud.',
        description: 'Onze DevOps aanpak en cloud expertise zorgen voor continue integratie, automatische deployments en proactieve monitoring. Profiteer van verbeterde stabiliteit, snellere releases en geoptimaliseerde infrastructuurkosten. We maken gebruik van moderne cloud platforms voor maximale betrouwbaarheid.',
        image: 'https://images.unsplash.com/photo-1603695762547-fba8b92eace9?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/devops-cloud',
        ctaText: 'DevOps & Cloud diensten'
      },
      {
        title: 'UX/UI Design',
        shortDescription: 'Gebruiksvriendelijke interfaces die conversie verhogen.',
        description: 'Onze UX/UI designers creëren intuïtieve en aantrekkelijke interfaces die gebruikers betrekken en conversies stimuleren. We combineren creativiteit met data-gedreven inzichten voor het beste resultaat. Van wireframes tot high-fidelity prototypes - gebruikerservaring staat centraal.',
        image: 'https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/ux-design',
        ctaText: 'UX/UI design diensten'
      },
      {
        title: 'Custom Development',
        shortDescription: 'Op maat gemaakte software oplossingen.',
        description: 'Wanneer standaard software niet voldoet, bouwen wij een oplossing specifiek voor uw unieke uitdagingen. Van concept tot realisatie leveren we hoogwaardige software met een focus op gebruiksvriendelijkheid en efficiëntie. Elke regel code wordt geschreven met uw specifieke behoeften in gedachten.',
        image: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800&auto=format&fit=crop&q=80',
        ctaLink: '/oplossingen/custom-development',
        ctaText: 'Custom software oplossingen'
      }
    ]
  }
];

const isModalOpen = ref(false)
const selectedFeature = ref({})

function openModal(feature) {
  selectedFeature.value = feature
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
}

// Add reactive state for hover management
const hoveredImageIndex = ref(null);

// Function to handle image hover
const handleImageHover = (index) => {
  hoveredImageIndex.value = index;
};

// Function to handle image leave
const handleImageLeave = () => {
  hoveredImageIndex.value = null;
};

// Show 3 random testimonials with constraints
const allTestimonials = computed(() => {
  // Filter products that have testimonials with the Q&A format
  const productsWithTestimonials = products.filter(product => 
    product.testimonial && 
    (product.testimonial.answer1 || product.testimonial.quote) && // Support both formats
    product.testimonial.author
  );
  
  // Separate special products (ids 1, 2, 3) from others
  const specialProducts = productsWithTestimonials.filter(product => 
    [1, 2, 3].includes(product.meta.id)
  );
  
  const otherProducts = productsWithTestimonials.filter(product => 
    ![1, 2, 3].includes(product.meta.id)
  );
  
  // Create our result array
  let selectedProducts = [];
  
  // Randomly decide whether to include a special product (50% chance)
  const includeSpecialProduct = Math.random() > 0.5 && specialProducts.length > 0;
  
  if (includeSpecialProduct) {
    // Randomly pick one special product
    const randomSpecialIndex = Math.floor(Math.random() * specialProducts.length);
    selectedProducts.push(specialProducts[randomSpecialIndex]);
    
    // Fill the remaining slots with other products
    const shuffledOther = [...otherProducts].sort(() => 0.5 - Math.random());
    selectedProducts = selectedProducts.concat(shuffledOther.slice(0, 2));
  } else {
    // Only use other products
    const shuffledOther = [...otherProducts].sort(() => 0.5 - Math.random());
    selectedProducts = shuffledOther.slice(0, 3);
  }
  
  // Fallback if we don't have enough products (ensuring we still follow the constraint)
  if (selectedProducts.length < 3) {
    // Check if we already have a special product
    const hasSpecial = selectedProducts.some(product => [1, 2, 3].includes(product.meta.id));
    
    // Get available products that follow our constraint
    const availableForFallback = hasSpecial 
      ? otherProducts
      : productsWithTestimonials;
      
    // Add more products if available
    const notYetSelected = availableForFallback.filter(product => 
      !selectedProducts.some(selected => selected.meta.id === product.meta.id)
    );
    
    const shuffledRemaining = [...notYetSelected].sort(() => 0.5 - Math.random());
    const needed = 3 - selectedProducts.length;
    selectedProducts = selectedProducts.concat(shuffledRemaining.slice(0, needed));
  }
  
  return selectedProducts;
});
</script>

<template>
    <!-- Apple-style Light Product Hero Section -->
    <div class="relative w-full min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-50/50 overflow-hidden">
        <!-- Ambient light effects -->
        <div class="absolute inset-0">
            <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-mint-100/30 rounded-full blur-3xl"></div>
            <div class="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-100/20 rounded-full blur-3xl"></div>
        </div>
        
        <!-- Top fade overlay for seamless integration -->
        <div class="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white via-white/80 to-transparent z-20"></div>
        
        <div ref="heroStartAnimation" class="relative z-10 wide-container">
            <!-- Hero Product Showcase -->
            <div class="pt-16 text-center">                
                <!-- Product Title -->
                <h1 ref="heroTextRef" class="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-thin text-zinc-900 tracking-tight leading-[1.1] mb-6">
                    <span class="block font-light">De toekomst van</span>
                    <span class="block font-bold bg-text-mint text-transparent bg-clip-text">software ontwikkeling</span>
                </h1>
                
                <!-- Hero Product Image with integrated fade effects -->
                <div class="relative py-20">
                    <!-- Top image fade overlay -->
                    <div ref="heroOverlayRef" class="absolute -top-2 -left-2 -right-2 -bottom-2 bg-gradient-to-b from-zinc-50 via-white/60 to-transparent z-30"></div>
                    
                    <div ref="heroImageContainerRef" class="relative w-full max-w-6xl mx-auto ring-mint-500 ring-2 p-2 rounded-md bg-mint-200/10 backdrop-blur-3xl">                        
                        <!-- Image with custom fade mask -->
                        <img ref="heroImageRef" :src="'/img/codes_software_development_showcase.webp'" alt="Innovatieve softwareoplossingen showcase" class="w-full h-auto object-contain rounded-lg border-2 border-zinc-50" />
                    </div>
                </div>
                
                <!-- Product Description -->
                <div class="max-w-4xl mx-auto mb-16">
                    <p ref="heroSubtitleRef" class="text-xl md:text-2xl text-zinc-600 leading-relaxed mb-8">Ontworpen voor prestaties. Gebouwd voor de toekomst.<br /><span class="text-zinc-900 font-medium">Innovatieve platforms die uw bedrijfsprocessen transformeren.</span></p>
                </div>
                
                <!-- Call to Action Buttons -->
                <div ref="heroCtaRef" class="flex flex-col sm:flex-row gap-4 justify-center mb-20">
                    <Button href="/contact" variant="gradient" icon="arrow-right" iconPosition="right" size="large" class="bg-gradient-to-r from-mint-500 to-mint-600 hover:from-mint-400 hover:to-mint-500 shadow-xl hover:shadow-mint-500/25 transition-all duration-300">
                        Ontdek jouw oplossing
                    </Button>
                    <Button href="/#software-solutions" variant="glass" icon="play" iconPosition="left" size="large" class="bg-white/70 backdrop-blur-md border-zinc-200/50 text-zinc-700 hover:bg-white/90 shadow-lg transition-all duration-300">
                        Bekijk showcase
                    </Button>
                </div>
                
                <!-- Product Features Grid -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-20">
                    <div class="group text-center p-8 bg-white/70 backdrop-blur-md rounded-2xl border border-zinc-200/50 hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div class="w-16 h-16 bg-gradient-to-br from-mint-100 to-mint-200 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                            <font-awesome-icon :icon="['fas', 'rocket']" class="text-mint-600 text-2xl" />
                        </div>
                        <h3 class="text-xl font-semibold text-zinc-900 mb-3">Razendsnel</h3>
                        <p class="text-zinc-600 leading-relaxed">Van concept tot productie in weken, niet maanden.</p>
                    </div>
                    
                    <div class="group text-center p-8 bg-white/70 backdrop-blur-md rounded-2xl border border-zinc-200/50 hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div class="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                            <font-awesome-icon :icon="['fas', 'shield-alt']" class="text-blue-600 text-2xl" />
                        </div>
                        <h3 class="text-xl font-semibold text-zinc-900 mb-3">Veilig</h3>
                        <p class="text-zinc-600 leading-relaxed">Enterprise-grade beveiliging als standaard.</p>
                    </div>
                    
                    <div class="group text-center p-8 bg-white/70 backdrop-blur-md rounded-2xl border border-zinc-200/50 hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300">
                        <div class="w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                            <font-awesome-icon :icon="['fas', 'infinity']" class="text-purple-600 text-2xl" />
                        </div>
                        <h3 class="text-xl font-semibold text-zinc-900 mb-3">Schaalbaar</h3>
                        <p class="text-zinc-600 leading-relaxed">Groeit mee van startup tot enterprise.</p>
                    </div>
                </div>
                
                <!-- Contact Information -->
                <div ref="heroContactInfoRef" class="text-center">
                    <div class="flex flex-col lg:flex-row items-center justify-center gap-8">
                        <Button href="tel:0850160117" variant="glass" :useInertia="false" 
                                class="bg-white/70 backdrop-blur-md border-zinc-200/50 text-zinc-700 hover:bg-white/90 shadow-lg">
                            <div class="flex items-center gap-4">
                                <div class="flex items-center">
                                    <div class="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                                    <font-awesome-icon :icon="['fas', 'phone']" class="h-4 w-4 mr-2" />
                                    <span class="font-medium">085 01 60 117</span>
                                </div>
                                <div class="hidden sm:block h-4 w-px bg-zinc-300"></div>
                                <span class="hidden sm:block text-zinc-500">Direct bereikbaar</span>
                            </div>
                        </Button>
                        
                        <div class="flex items-center gap-6 text-sm text-zinc-500">
                            <div class="flex items-center gap-2">
                                <font-awesome-icon :icon="['fas', 'users']" class="h-4 w-4" />
                                <span>200+ klanten</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <font-awesome-icon :icon="['fas', 'star']" class="h-4 w-4 text-yellow-500" />
                                <span>4.9/5 waardering</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Bottom fade to next section -->
        <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-20"></div>
    </div>
    
    <!-- Add the ServiceShowcase component here -->
    <ServiceShowcase :categories="serviceCategories" />

    <!-- Bento Showcase Section -->
    <div class="bg-white dark:bg-zinc-900 transition-colors duration-500">
        <BentoShowcaseCards :products="products" />
    </div>

    <!-- Features Section -->
    <div id="platform-features" class="bg-zinc-900 dark:bg-white transition-colors duration-500 headerflip">
        <div class="wide-container">
            <hgroup class="flex flex-col items-center sm:text-center">
                <h2 class="text-pretty text-4xl font-bold text-white dark:text-zinc-900 sm:text-5xl lg:text-6xl tracking-tight transition-colors duration-300">Ontworpen voor maximale impact</h2>
                <p class="text-balance text-xl sm:font-medium text-zinc-300 dark:text-zinc-900 max-w-6xl mt-12 transition-colors duration-300">Bij <span class="text-nowrap">c-o-d-e-s</span> staan jouw behoeften centraal. Van flexibele platformfunctionaliteiten die jouw creativiteit ondersteunen tot geavanceerde beveiliging die jouw gegevens beschermt.</p>
            </hgroup>
            <div ref="featuresListRef" class="flex flex-col items-center">
                <div class="mx-auto mt-8 max-w-3xl lg:max-w-none">
                    <div class="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
                        <div v-for="(feature, index) in features" :key="feature.name" class="feature-item group flex flex-col bg-zinc-800/80 dark:bg-white backdrop-blur-sm rounded-xl p-4 sm:p-6 md:p-8 cursor-pointer transition-all duration-500 ease-in-out relative shadow-sm hover:shadow-md hover:shadow-black/20 dark:hover:shadow-black/10 overflow-hidden border border-zinc-700/50 dark:border-zinc-200/50 hover:ring-2 hover:ring-offset-2 ring-offset-zinc-900 hover:ring-azure-800 dark:hover:ring-azure-600 dark:hover:bg-zinc-50 hover:bg-zinc-700/60"
                                @click="openModal(feature)">
                            <dt class="text-3xl sm:text-4xl font-medium text-white dark:text-zinc-900 break-words group-hover:text-zinc-100 dark:group-hover:text-zinc-800 transition-all duration-500 ease-smooth relative z-10">
                                <div class="mb-6 flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-500 ease-smooth">
                                    <font-awesome-icon :icon="['fas', feature.icon]" class="h-8 w-8 text-azure-600 dark:text-azure-800 group-hover:text-azure-400 dark:group-hover:text-azure-600 group-hover:scale-125 transition-all duration-500 ease-smooth" aria-hidden="true" />
                                </div>
                                <span class="inline-block transition-all duration-500 ease-smooth group-hover:translate-x-2">
                                    {{ feature.name }}
                                </span>
                            </dt>
                            <dd class="mt-1 flex flex-auto flex-col text-base/7 text-zinc-300 dark:text-zinc-600 group-hover:text-zinc-200 dark:group-hover:text-zinc-700 transition-colors duration-300 relative z-10">
                                <p class="font-medium mr-12 mb-6 transition-all duration-500 ease-smooth group-hover:translate-x-2 group-hover:delay-[100ms]">{{ feature.shortDescription }}</p>
                                <div class="ml-auto mt-auto h-8 w-8 rounded-full bg-azure-600 dark:bg-azure-800 flex items-center justify-center transition-all duration-300 group-hover:scale-125 ease-smooth">
                                    <font-awesome-icon :icon="['fas', 'plus']" class="h-4 w-4 text-white transition-transform duration-300 ease-smooth" aria-hidden="true" />
                                </div>
                            </dd>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <TechStackSection />
    <TeamMembersSection />
    
    <!-- Testimonials Section -->
    <div ref="testimonialsStartAnimation" id="client-testimonials" class="py-24 bg-white dark:bg-zinc-900 transition-colors duration-500">
        <div class="wide-container">
            <div class="flex flex-col lg:flex-row items-start lg:items-end lg:justify-between w-full mb-12 gap-4">
                <hgroup class="flex flex-col grow">
                    <h2 ref="testimonialsTitleRef" class="text-pretty text-4xl font-bold text-zinc-900 dark:text-white sm:text-5xl lg:text-6xl tracking-tight transition-colors duration-300">Klantgesprekken</h2>
                    <p ref="testimonialsSubtitleRef" class="text-balance text-left text-xl sm:font-medium text-zinc-700 dark:text-zinc-300 max-w-3xl mr-auto mt-6 transition-colors duration-500">Ontdek hoe onze klanten hun bedrijfsprocessen hebben getransformeerd met onze maatwerkoplossingen.</p>
                </hgroup>
                <Button href="/interviews" variant="white" darkVariant="glass" icon="arrow-right" iconPosition="right" size="large" :block="true">
                    Bekijk alle klantgesprekken
                </Button>
            </div>
            
            <TestimonialCarousel :testimonials="allTestimonials" />
        </div>
    </div>

    <!-- Feature Modal -->
    <TransitionRoot appear :show="isModalOpen" as="template">
        <Dialog as="div" @close="closeModal" class="relative z-[100]">
            <!-- Improved backdrop transition -->
            <TransitionChild as="template" enter="duration-700 ease-out" enter-from="opacity-0" enter-to="opacity-100" leave="duration-300 ease-in" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
            </TransitionChild>

            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4">
                    <!-- Enhanced modal transition -->
                    <TransitionChild as="template" 
                        enter="transform transition duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]" 
                        enter-from="opacity-0 translate-y-8 scale-95" 
                        enter-to="opacity-100 translate-y-0 scale-100" 
                        leave="transform transition duration-300 ease-in" 
                        leave-from="opacity-100 scale-100" 
                        leave-to="opacity-0 scale-95 -translate-y-8">
                        <DialogPanel class="relative w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white dark:bg-zinc-800 shadow-2xl transition-all border border-zinc-100 dark:border-zinc-700">
                            <!-- Content container with proper spacing -->
                            <div class="p-8 sm:p-12">
                                <!-- Feature header section -->
                                <div class="mb-8">
                                    <!-- Feature category badge -->
                                    <div class="inline-flex items-center px-3 py-1 rounded-full bg-coral-50 dark:bg-coral-900/20 text-coral-600 dark:text-coral-400 text-sm font-medium mb-4 transition-colors duration-300">
                                        <font-awesome-icon :icon="['fas', selectedFeature.icon]" class="mr-2 h-3 w-3" />
                                        {{ selectedFeature.name }}
                                    </div>
                                    
                                    <!-- Feature title -->
                                    <h2 class="text-3xl sm:text-4xl lg:text-5xl font-semibold text-zinc-900 dark:text-white leading-tight mb-6 transition-colors duration-300">
                                        {{ selectedFeature.longName }}
                                    </h2>
                                    
                                    <!-- Feature description -->
                                    <div class="space-y-4">
                                        <p class="text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed transition-colors duration-300">
                                            {{ selectedFeature.longDescription }}
                                        </p>
                                        <p class="text-base text-zinc-500 dark:text-zinc-400 font-medium transition-colors duration-300">
                                            {{ selectedFeature.shortDescription }}
                                        </p>
                                    </div>
                                </div>
                                
                                <!-- Interactive image gallery -->
                                <div class="mt-8">
                                    <div class="flex max-md:flex-col justify-center gap-3 w-full">
                                        <article v-for="(showcase, i) in [selectedFeature.showcase[2], selectedFeature.showcase[1], selectedFeature.showcase[3]]"
                                                    :key="i" 
                                                    class="relative rounded-xl overflow-hidden cursor-pointer h-64 sm:h-80 transition-all duration-500 ease-[cubic-bezier(.5,.85,.25,1.15)] min-h-[50vh] max-h-[800px]"
                                                    :class="[
                                                    i === 0 ? 'w-full' : 'w-full md:w-1/3',
                                                    hoveredImageIndex !== null && hoveredImageIndex === i ? 'md:w-[60%]' : '',
                                                    hoveredImageIndex !== null && hoveredImageIndex !== i ? 'md:w-[20%]' : ''
                                                    ]"
                                                    @mouseenter="handleImageHover(i)"
                                                    @mouseleave="handleImageLeave"
                                                    :style="{ zIndex: hoveredImageIndex === i ? 10 : 1 }">
                                            <!-- Gradient overlay -->
                                            <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-300 z-10"
                                                    :class="hoveredImageIndex === i ? 'opacity-80' : 'opacity-60'"></div>
                                            
                                            <!-- Content overlay -->
                                            <div class="absolute inset-0 text-white z-20 p-6 flex flex-col justify-end">
                                                <h3 class="text-lg font-semibold mb-2 transition-all duration-300 delay-100"
                                                    :class="hoveredImageIndex === i || (i === 0 && hoveredImageIndex === null) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'">
                                                    {{ showcase.title }}
                                                </h3>
                                                <p class="text-sm text-white/90 transition-all duration-300 delay-200"
                                                    :class="hoveredImageIndex === i || (i === 0 && hoveredImageIndex === null) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'">
                                                    {{ showcase.description }}
                                                </p>
                                            </div>
                                            <!-- Image -->
                                            <img :src="showcase.image" :alt="showcase.alt" class="h-full w-full object-cover" />
                                        </article>
                                    </div>
                                </div>
                                
                                <!-- Action buttons -->
                                <div class="mt-8 flex flex-col sm:flex-row gap-4 justify-end">
                                    <Button @click="closeModal" variant="white" icon="arrow-left" iconPosition="left">
                                        Terug naar overzicht
                                    </Button>
                                    <Button href="/contact" variant="gradient" icon="arrow-right" iconPosition="right">
                                        Meer informatie
                                    </Button>
                                </div>
                            </div>
                            
                            <!-- Close button -->
                            <button @click="closeModal" class="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/90 dark:bg-zinc-700/90 backdrop-blur-sm border border-zinc-200 dark:border-zinc-600 flex items-center justify-center hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:ring-offset-2">
                                <font-awesome-icon :icon="['fas', 'xmark']" class="h-5 w-5 text-zinc-600 dark:text-zinc-300 transition-colors duration-200" />
                            </button>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- CTA Section -->
    <div ref="footerStartTextAnimation" class="bg-zinc-900 dark:bg-white transition-colors duration-500 min-h-[60vh] flex items-center headerflip">
        <div class="wide-container">
            <div class="flex flex-col lg:flex-row items-start lg:items-end lg:justify-between w-full gap-4">
                <hgroup class="flex flex-col grow">
                    <h2 ref="footerTitleRef" class="text-pretty text-4xl font-bold text-white dark:text-zinc-900 sm:text-5xl lg:text-6xl tracking-tight transition-colors duration-500">
                        Klaar voor de <span class="bg-text-coral text-transparent bg-clip-text">volgende stap</span> in digitale transformatie?
                    </h2>
                    <p ref="footerSubtitleRef" class="text-balance text-left text-xl sm:font-medium text-zinc-300/80 dark:text-zinc-900/80 max-w-3xl mr-auto mt-6 transition-colors duration-500">
                        Onze software-oplossingen helpen u om uw bedrijfsprocessen te optimaliseren, kosten te besparen en effectiever te werken.
                    </p>
                </hgroup>
                    
                <div ref="footerButtonRef" class="flex flex-col sm:flex-row gap-4">
                    <Button href="/contact" variant="gradient" icon="headset" iconPosition="left" size="large">
                        Plan een vrijblijvend gesprek
                    </Button>
                    <Button href="/over-ons" variant="white" darkVariant="glass" icon="info-circle" iconPosition="right" size="large">
                        Meer over onze aanpak
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Enhanced feature card transitions */
.feature-item {
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* Smooth glass morphism effects for light mode */
.backdrop-blur-dynamic {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

/* Custom image fade integration */
.hero-image-fade {
  mask-image: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%);
}
</style>